import * as THREE from 'three';
import { FIST_VIEWMODEL, PLATE_VIEWMODEL as V } from '../config/viewmodel';
import type { Food } from '../config/aamupala';
import type { AamupalaView } from '../world/Aamupala';

export interface PlateFrame {
  dt: number;
  visible: boolean;
  breakfast: AamupalaView | null;
  speed: number;
  bobLateral: number;
  bobVertical: number;
  swayIntensity: number;
}

/** Camera-attached left hand. Reads the breakfast state without changing it. */
export class PlateViewmodel {
  private readonly pivot = new THREE.Group();
  private readonly food = new THREE.Group();
  private readonly sphere = new THREE.SphereGeometry(1, 12, 8);
  private readonly box = new THREE.BoxGeometry(1, 1, 1);
  private readonly dish = new THREE.CylinderGeometry(V.radius, V.radius * 0.85, 0.016, 32);
  private readonly rim = new THREE.TorusGeometry(V.radius * 0.96, 0.007, 8, 32);
  private readonly materials = new Map<number, THREE.MeshBasicMaterial>();
  private served: readonly Food[] = [];
  private shown = 0;
  private bob = 0;

  constructor(private readonly camera: THREE.PerspectiveCamera) {
    this.pivot.name = 'breakfast-plate';
    this.food.name = 'breakfast-servings';
    // Explicit layers keep the hand below the plate with world depth testing off.
    const sleeve = this.mesh(this.box, FIST_VIEWMODEL.sleeveColor, 996);
    sleeve.scale.set(0.065, 0.065, 0.19);
    sleeve.position.set(-0.045, -0.085, 0.16);
    sleeve.rotation.x = -0.28;
    const palm = this.mesh(this.box, FIST_VIEWMODEL.skin, 997);
    palm.scale.set(0.07, 0.038, 0.10);
    palm.position.set(-0.04, -0.034, 0.066);
    const plate = this.mesh(this.dish, V.underside, 998);
    const bed = this.mesh(this.sphere, V.ceramic, 999);
    bed.scale.set(V.radius * 0.94, 0.008, V.radius * 0.94);
    bed.position.y = 0.009;
    const rim = this.mesh(this.rim, V.rim, 1000);
    rim.rotation.x = -Math.PI / 2;
    rim.position.y = 0.012;
    this.pivot.add(sleeve, palm, plate, bed, rim, this.food);
    this.pivot.visible = false;
    camera.add(this.pivot);
  }

  reset(): void {
    this.shown = 0;
    this.bob = 0;
    this.served = [];
    this.food.clear();
    this.pivot.visible = false;
  }

  update(frame: PlateFrame): void {
    const breakfast = frame.breakfast;
    const served = breakfast?.served ?? [];
    if (served.length !== this.served.length || served.some((food, i) => food !== this.served[i])) {
      this.served = [...served];
      this.fillPlate();
    }
    const holding = breakfast !== null && breakfast.phase !== 'idle';
    const dt = Math.max(0, frame.dt);
    if (frame.visible) {
      const rate = dt / V.raiseDuration;
      this.shown += THREE.MathUtils.clamp((holding ? 1 : 0) - this.shown, -rate, rate);
      this.bob += dt * Math.min(frame.speed, V.bobSpeedCap) * V.bobRate;
    }
    this.pivot.visible = frame.visible && holding && this.shown > 0.002;
    if (!this.pivot.visible) return;

    const halfHeight = Math.tan(THREE.MathUtils.degToRad(this.camera.fov) / 2) * V.depth;
    const sway = frame.swayIntensity;
    this.pivot.position.set(
      (V.x + Math.sin(this.bob) * V.bobAmount + frame.bobLateral * sway) * halfHeight * this.camera.aspect,
      (V.y - (1 - this.shown) * V.raiseFrom + Math.sin(this.bob * 2) * V.bobAmount + frame.bobVertical * sway) * halfHeight,
      -V.depth,
    );
    // Scale with FOV and narrow windows so the whole plate stays in the left corner.
    this.pivot.scale.setScalar(halfHeight / V.depth * Math.min(1, this.camera.aspect));
    this.pivot.rotation.set(V.pitch, 0, V.roll);
    for (const material of this.materials.values()) material.opacity = this.shown;
  }

  dispose(): void {
    this.pivot.removeFromParent();
    for (const geometry of [this.sphere, this.box, this.dish, this.rim]) geometry.dispose();
    for (const material of this.materials.values()) material.dispose();
  }

  private mesh(geometry: THREE.BufferGeometry, color: number, order: number): THREE.Mesh {
    let material = this.materials.get(color);
    if (!material) {
      material = new THREE.MeshBasicMaterial({
        color, transparent: true, depthTest: false, depthWrite: false, fog: false,
      });
      this.materials.set(color, material);
    }
    const mesh = new THREE.Mesh(geometry, material);
    mesh.renderOrder = order;
    mesh.frustumCulled = false;
    return mesh;
  }

  private fillPlate(): void {
    this.food.clear();
    this.served.forEach((food, index) => {
      const portion = new THREE.Group();
      const angle = index / Math.max(3, this.served.length) * Math.PI * 2;
      portion.position.set(Math.cos(angle) * 0.062, 0.028, Math.sin(angle) * 0.062);
      if (food === 'pulled-pork') {
        for (let i = 0; i < 7; i++) {
          const shred = this.mesh(this.sphere, V.pork, 1001);
          shred.scale.set(0.027, 0.008, 0.008);
          shred.position.set((i % 3 - 1) * 0.014, Math.floor(i / 3) * 0.005, (i - 3) * 0.007);
          shred.rotation.y = i * 1.8;
          portion.add(shred);
        }
      } else {
        const meat = this.mesh(this.sphere, food === 'makkara' ? V.sausage : V.steak, 1001);
        if (food === 'makkara') meat.scale.set(0.053, 0.016, 0.018);
        else meat.scale.set(0.043, 0.013, 0.033);
        meat.rotation.y = angle + 0.4;
        portion.add(meat);
      }
      this.food.add(portion);
    });
  }
}
